import { useEffect, useRef, useState } from "react";
import { FileText, Loader2, Play } from "lucide-react";
import { toast } from "sonner";
import { PreviewDialog, fetchBlob, canPreview, download } from "./Files";

const URL_RE = /(https?:\/\/[^\s]+)/g;

export function Linkified({ text, highlight, mentions = [] }) {
  const names = mentions.map((x) => x.name).filter(Boolean).sort((a, b) => b.length - a.length);
  const tagRe = names.length ? new RegExp(`(@(?:${names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")}))`, "g") : null;
  const hl = (s, k) => {
    if (!highlight) return <span key={k}>{s}</span>;
    const parts = s.split(new RegExp(`(${highlight.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig"));
    return <span key={k}>{parts.map((p, i) => (p.toLowerCase() === highlight.toLowerCase() ? <mark key={i} className="rounded bg-yellow-200 px-0.5">{p}</mark> : p))}</span>;
  };
  const mark = (s, k) => (tagRe ? <span key={k}>{s.split(tagRe).map((p, i) => (i % 2 ? <span key={i} data-testid="chat-mention-tag" className="rounded bg-[#15966B]/10 px-0.5 font-semibold text-[#15966B]">{p}</span> : hl(p, i)))}</span> : hl(s, k));
  return <>{String(text || "").split(URL_RE).map((p, i) => (/^https?:\/\//.test(p) ? <a key={i} href={p} target="_blank" rel="noreferrer" className="break-all text-[#A9792C] underline">{p}</a> : mark(p, i)))}</>;
}

function useBlob(file) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    fetchBlob(file).then((x) => { u = x; setUrl(x); }).catch(() => {});
    return () => u && URL.revokeObjectURL(u);
  }, [file.id]); // eslint-disable-line react-hooks/exhaustive-deps
  return url;
}

function AudioNote({ file, testid }) {
  const url = useBlob(file);
  return url ? <audio data-testid={testid} controls src={url} className="mt-1 h-10 w-64 max-w-full" /> : <Loader2 className="mt-1 h-4 w-4 animate-spin text-zinc-300" />;
}

function ImageThumb({ file, testid, onOpen }) {
  const url = useBlob(file);
  return (
    <button data-testid={`${testid}-image`} onClick={onOpen} className="mt-1 block overflow-hidden rounded-lg bg-zinc-100">
      {url ? <img src={url} alt={file.original_filename} className="max-h-72 w-full max-w-[280px] object-cover transition-transform hover:scale-[1.02]" /> : <div className="flex h-40 w-56 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-zinc-300" /></div>}
    </button>
  );
}

function VideoClip({ file, testid }) {
  const [on, setOn] = useState(false);
  const url = useBlob(file);
  if (!on) return <button data-testid={`${testid}-video`} onClick={() => setOn(true)} className="mt-1 flex h-40 w-64 max-w-full items-center justify-center rounded-lg bg-zinc-900 text-white"><Play className="h-10 w-10" /></button>;
  return url ? <video data-testid={`${testid}-video-player`} controls autoPlay src={url} className="mt-1 max-h-72 w-72 max-w-full rounded-lg bg-black" /> : <Loader2 className="mt-1 h-5 w-5 animate-spin" />;
}

export function Attachment({ a, testid, by = "" }) {
  const [preview, setPreview] = useState(null);
  const file = { id: a.id, original_filename: a.name, content_type: a.content_type, size: a.size || 0, uploaded_by: by };
  const ct = a.content_type || "";
  if (ct.startsWith("audio/")) return <AudioNote file={file} testid={`${testid}-audio`} />;
  if (ct.startsWith("video/")) return <VideoClip file={file} testid={testid} />;
  const open = async () => {
    if (canPreview(file)) return setPreview(file);
    download(await fetchBlob(file), a.name);
  };
  return (
    <>
      {ct.startsWith("image/") ? <ImageThumb file={file} testid={testid} onOpen={open} /> : (
        <button data-testid={`${testid}-file`} onClick={open} className="mt-1 flex w-64 max-w-full items-center gap-2 rounded-lg bg-black/5 px-3 py-2.5 text-left text-xs transition-colors hover:bg-black/10">
          <FileText className="h-6 w-6 shrink-0 text-[#A9792C]" />
          <span className="min-w-0"><span className="block truncate font-medium text-zinc-800">{a.name}</span><span className="text-[10px] uppercase text-zinc-500">{(a.name.split(".").pop() || "file")}{a.size ? ` · ${Math.max(1, Math.round(a.size / 1024))} KB` : ""}</span></span>
        </button>
      )}
      <PreviewDialog file={preview} onClose={() => setPreview(null)} />
    </>
  );
}

export function useRecorder(onDone) {
  const [rec, setRec] = useState(null);
  const [secs, setSecs] = useState(0);
  const chunks = useRef([]);
  const cancelled = useRef(false);
  useEffect(() => {
    if (!rec) return undefined;
    const t = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [rec]);
  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunks.current = [];
      cancelled.current = false;
      mr.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        if (!cancelled.current) onDone(new Blob(chunks.current, { type: mr.mimeType || "audio/webm" }));
      };
      mr.start();
      setSecs(0);
      setRec(mr);
    } catch {
      toast.error("Microphone access was blocked. Allow mic permission in your browser.");
    }
  };
  const stop = (cancel = false) => { cancelled.current = cancel; rec?.stop(); setRec(null); };
  return { recording: !!rec, secs, start, stop };
}
